import { useSyncExternalStore } from "react";

/**
 * 共享 Token Provider（docs/embedded-workbench-design.md §9）。
 *
 * 请求层、SignalR、附件上传下载和会话查询都必须从这里取 Token，
 * 不允许任何调用方再直接读 `OidcClient`——嵌入端根本不在 `OidcProvider` 之内。
 */

export type TokenRequestReason = "missing" | "expiring" | "unauthorized";

/**
 * 身份作用域：替代原先从 ID Token 推导的会话缓存 key。
 * 独立端来自 ID Token claims，嵌入端来自交换 Token 的 `sub` + `exp`。
 */
export type IdentityScope = { key: string; expiresAt: number } | null;

export interface AccessTokenProvider {
  /** 当前可用的 Access Token；没有就返回 null，绝不在这里触发登录。 */
  getAccessToken(): Promise<string | null>;
  /** 主动索取新 Token（续期或 401 之后）。并发调用必须合并为同一个 Promise。 */
  requestAccessToken(reason: TokenRequestReason): Promise<string | null>;
  /** Token 变化通知，供 `useSyncExternalStore` 使用。 */
  subscribe(listener: () => void): () => void;
  /** 会话缓存与 Query Key 的作用域来源，同一 Token 必须返回同一个对象引用。 */
  getIdentityScope(): IdentityScope;
  /** 当前会话已过期、等待用户操作时暂停请求；未实现的 Provider 沿用自身会话生命周期。 */
  isSessionExpired?(): boolean;
  /**
   * 服务端会话过期或换票后仍然 401，认证已无法恢复。
   * standalone 提示用户选择是否重新登录；embedded 结束嵌入会话并通知宿主。
   * 两端都不能在此自动导航到 IAM。
   */
  onAuthenticationFailed(reason: TokenRequestReason): Promise<void>;
  /** 丢弃内存中的 Token 与派生状态。 */
  clear(): void;
}

let currentProvider: AccessTokenProvider | null = null;

/**
 * 由 `main.tsx` 在渲染 React 之前按 surface 调用一次。
 * 这里刻意不提供默认实现：没设置就让请求失败，绝不回退到 OIDC，
 * 否则嵌入端会在 iframe 里跳 IAM 登录页（AR-28）。
 */
export function setAccessTokenProvider(provider: AccessTokenProvider): void {
  currentProvider = provider;
}

export function getAccessTokenProvider(): AccessTokenProvider {
  if (!currentProvider) {
    throw new Error("AccessTokenProvider 尚未装配：main.tsx 必须在渲染前按 surface 调用 setAccessTokenProvider。");
  }
  return currentProvider;
}

export function hasAccessTokenProvider(): boolean {
  return currentProvider !== null;
}

// useSyncExternalStore 要求订阅与读取函数引用稳定，因此提在模块级。
const subscribeToProvider = (listener: () => void) => getAccessTokenProvider().subscribe(listener);
const readIdentityScope = () => getAccessTokenProvider().getIdentityScope();

/** 当前身份作用域。两端语义一致，`SessionProvider` 据此隔离缓存与 Query Key。 */
export function useIdentityScope(): IdentityScope {
  return useSyncExternalStore(subscribeToProvider, readIdentityScope, readIdentityScope);
}
